import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Input, Text } from '@chakra-ui/react';
import { Search, ArrowForward } from '@mui/icons-material';

import FloatingDrawer from './FloatingDrawer';
import ProductCard from '../product/ProductCard';
import { useShopUI } from '../../contexts/ShopUIContext';
import useCatalog from '../../hooks/useCatalog';

const MOST_SEARCHED = ['Shirts', 'Jeans', 'Jackets', 'Blazers', 'Tees'];

// Search panel (design-system.md §4.6): input, Most Searched, live product matches, SEE ALL RESULTS
const SearchPanel = () => {

    const navigate = useNavigate();
    const inputRef = useRef();
    const { searchOpen, closeSearch } = useShopUI();
    const { categories, products } = useCatalog();
    const [q, setQ] = useState('');

    const matches = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return [...products].reverse().slice(0, 4);
        const catName = Object.fromEntries(categories.map(c => [c._id, c.name.toLowerCase()]));
        return products.filter(p => `${p.name} ${p.color} ${catName[p.category] || ''} ${p.description}`.toLowerCase().includes(term)).slice(0, 6);
    }, [q, products, categories]);

    const submit = (term = q) => {
        const t = term.trim();
        closeSearch();
        navigate('/search', { state: t ? { q: t } : {} });
    };

    const footer = (
        <Button variant='ink' width='100%' height='48px' fontSize='16px' rightIcon={<ArrowForward fontSize='small' />} onClick={() => submit()} >
            {q.trim() ? 'See all results' : 'Shop all'}
        </Button>
    );

    return (
        <FloatingDrawer isOpen={searchOpen} onClose={closeSearch} title='Search' width='470px' footer={footer} initialFocusRef={inputRef} label='Search' >
            <Box px={{ base: '16px', md: '20px' }} py='16px' >
                <Box as='form' role='search' onSubmit={(e) => { e.preventDefault(); submit(); }} display='flex' alignItems='center' height='48px' px='16px' gap='8px' border='1px solid' borderColor='raano.black' borderRadius='searchPill' >
                    <Search sx={{ fontSize: 22 }} />
                    <Input ref={inputRef} variant='unstyled' type='search' enterKeyHint='search' aria-label='Search products' placeholder='Search for shirts, jeans, jackets…' fontSize='15px' value={q} onChange={(e) => setQ(e.target.value)} sx={{ '&::-webkit-search-cancel-button': { display: 'none' } }} />
                    {q && <Text as='button' type='button' fontSize='12px' color='raano.muted' onClick={() => { setQ(''); inputRef.current?.focus(); }} >Clear</Text>}
                </Box>

                {!q.trim() &&
                    <Box mt='22px' pb='16px' borderBottom='1px solid' borderColor='raano.border' >
                        <Text fontSize='15px' fontWeight={600} mb='8px' >Most Searched</Text>
                        {MOST_SEARCHED.map(term => <Text key={term} as='button' display='block' fontSize='14px' color='blackAlpha.700' py='4px' _hover={{ color: 'raano.black' }} onClick={() => submit(term)} >{term}</Text>)}
                    </Box>}

                <Text mt='18px' mb='12px' fontSize='14px' fontWeight={700} textTransform='uppercase' sx={{ fontStretch: '75%' }} >
                    {q.trim() ? (matches.length ? `Products (${matches.length})` : 'No matching products') : 'Products'}
                </Text>
                <Box display='grid' gridTemplateColumns='1fr 1fr' columnGap='8px' rowGap='20px' >
                    {matches.map(p => <Box key={p._id} onClickCapture={closeSearch} ><ProductCard product={p} /></Box>)}
                </Box>
            </Box>
        </FloatingDrawer>
    );
};

export default SearchPanel;
